import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Receipt as ReceiptIcon, Trash2, Edit, TrendingUp, Sparkles } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useIsMobile } from '../hooks/useIsMobile';
import { notifyDataChanged } from '../utils/dataBus';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const Depenses = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile(640);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    categorie: '',
    montant: '',
    date: new Date().toISOString().split('T')[0],
    modePaiement: 'Especes',
    note: '',
    pjBase64: ''
  });
  
  // Filtrage mensuel
  const [selectedMonth, setSelectedMonth] = useState('current'); // 'current', 'previous' ou 'custom'
  const [customMonth, setCustomMonth] = useState(null); // { year: 2024, month: 10 }
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);

  const getMonthDates = (monthType) => {
    const now = new Date();
    let start, end;
    
    if (monthType === 'current') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    } else if (monthType === 'previous') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    } else if (monthType === 'custom' && customMonth) {
      start = new Date(customMonth.year, customMonth.month - 1, 1);
      end = new Date(customMonth.year, customMonth.month, 0, 23, 59, 59);
    }
    
    return { start, end };
  };

  const getAvailableMonths = () => {
    const months = [];
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const previousYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    
    for (let i = 0; i < 12; i++) {
      const targetDate = new Date(currentYear, currentMonth - 1 - i, 1);
      const year = targetDate.getFullYear();
      const month = targetDate.getMonth() + 1;
      
      if ((year === currentYear && month === currentMonth) || 
          (year === previousYear && month === previousMonth)) {
        continue;
      }
      
      months.push({
        year,
        month,
        label: targetDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      });
    }
    
    return months.reverse();
  };

  useEffect(() => {
    fetchExpenses();
    fetchCommercials();
    fetchCategories();
  }, [selectedMonth]);

  const fetchCommercials = async () => {
    try {
      const response = await axios.get(`${API}/commercials`, { withCredentials: true });
      setCommercials(response.data);
    } catch (error) {
      console.error('Erreur commerciaux:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`, { withCredentials: true });
      setCategories(response.data);
      if (response.data.length > 0 && !formData.categorie) {
        setFormData(prev => ({ ...prev, categorie: response.data[0].nom }));
      }
    } catch (error) {
      console.error('Erreur catégories:', error);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/expenses`, { withCredentials: true });
      let allExpenses = response.data;
      
      // Filtrage mensuel
      if (selectedMonth) {
        const { start, end } = getMonthDates(selectedMonth);
        allExpenses = allExpenses.filter(expense => {
          const expenseDate = new Date(expense.date);
          return expenseDate >= start && expenseDate <= end;
        });
      }
      
      // Sort by date: most recent first
      const sortedExpenses = allExpenses.sort((a, b) => new Date(b.date) - new Date(a.date));
      setExpenses(sortedExpenses);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedAuthor) {
      toast.error('Veuillez sélectionner l\'auteur de la dépense');
      return;
    }
    
    try {
      let authorName = 'CEO';
      if (selectedAuthor !== 'ceo') {
        const author = commercials.find(c => c.id === selectedAuthor);
        authorName = author?.nom || 'Non spécifié';
      }
      
      const expenseData = {
        ...formData,
        montant: parseInt(formData.montant),
        date: new Date(formData.date).toISOString(),
        createdBy: selectedAuthor,
        createdByName: authorName
      };
      
      if (isEditing && selectedExpense) {
        await axios.put(`${API}/expenses/${selectedExpense.id}`, expenseData, { withCredentials: true });
        toast.success('Dépense modifiée');
      } else {
        await axios.post(`${API}/expenses`, expenseData, { withCredentials: true });
        toast.success('Dépense ajoutée');
      }
      notifyDataChanged('depense');
      
      setShowDialog(false);
      setIsEditing(false);
      setFormData({
        categorie: 'Autres',
        montant: '',
        date: new Date().toISOString().split('T')[0],
        modePaiement: 'Especes',
        note: '',
        pjBase64: ''
      });
      setSelectedAuthor(null);
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(isEditing ? 'Erreur de modification' : 'Erreur d\'ajout');
    }
  };

  const handleEditExpense = (expense) => {
    setIsEditing(true);
    setSelectedExpense(expense);
    setFormData({
      categorie: expense.categorie,
      montant: expense.montant.toString(),
      date: new Date(expense.date).toISOString().split('T')[0],
      modePaiement: expense.modePaiement,
      note: expense.note || '',
      pjBase64: expense.pjBase64 || ''
    });
    setSelectedAuthor(expense.createdBy);
    setShowDialog(true);
  };

  const handleAddNew = () => {
    setIsEditing(false);
    setSelectedExpense(null);
    setFormData({
      categorie: 'Autres',
      montant: '',
      date: new Date().toISOString().split('T')[0],
      modePaiement: 'Especes',
      note: '',
      pjBase64: ''
    });
    setSelectedAuthor(null);
    setShowDialog(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, pjBase64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async () => {
    if (!selectedExpense) return;
    
    try {
      await axios.delete(`${API}/expenses/${selectedExpense.id}`, { withCredentials: true });
      toast.success('Dépense supprimée');
      notifyDataChanged('depense');
      setShowDeleteDialog(false);
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur de suppression');
    }
  };

  // Calculate total expenses
  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.montant, 0);

  return (
    <div className="space-y-6 pb-20 xl:pb-8" data-testid="depenses-page">
      {/* Header ULTRA LUX & WAHOOO - RESPONSIVE */}
      <div className="relative overflow-hidden">
        {/* Animated background glow */}
        <div className="absolute -inset-2 bg-gradient-to-r from-amber-300/20 via-yellow-300/20 to-amber-300/20 rounded-2xl blur-3xl animate-pulse"></div>
        
        <div className="relative backdrop-blur-xl bg-gradient-to-br from-[#1C2541]/95 via-[#2A3B5C]/95 to-[#1C2541]/95 p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 border-[#C6A15B]/40 shadow-2xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="space-y-1 sm:space-y-2">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 drop-shadow-[0_0_30px_rgba(252,211,77,0.5)] animate-pulse">
                <Sparkles className="inline-block w-6 h-6 sm:w-8 sm:h-8 mr-2 text-amber-300 animate-spin" style={{ animationDuration: '3s' }} />
                Dépenses & Charges
              </h1>
              <p className="text-amber-200/80 text-sm sm:text-base lg:text-lg font-semibold">Gestion luxueuse de vos dépenses</p>
            </div>
            <Button
              data-testid="add-expense-btn"
              onClick={handleAddNew}
              className="w-full sm:w-auto bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 hover:from-amber-500 hover:via-yellow-500 hover:to-amber-500 text-[#0B132B] font-black text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 shadow-2xl shadow-amber-400/50 hover:scale-105 sm:hover:scale-110 hover:rotate-1 transition-all duration-300 border-2 border-amber-300/50"
            >
              <Plus className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
              Nouvelle Dépense
            </Button>
          </div>
        </div>
      </div>

      {/* Filtrage Mensuel ULTRA PREMIUM - RESPONSIVE */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 via-yellow-400/20 to-amber-400/20 rounded-2xl sm:rounded-3xl blur-2xl animate-pulse"></div>
        <Card className="relative glass-effect border-2 sm:border-3 border-amber-400/50 shadow-2xl">
          <CardContent className="p-3 sm:p-4 md:p-6">
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-2 sm:gap-3">
              {/* Dropdown Premium "Choisir Un Mois" - RESPONSIVE */}
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                  className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-sm sm:text-base transition-all duration-300 transform hover:scale-105 ${
                    selectedMonth === 'custom'
                      ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 text-[#0B132B] shadow-[0_0_40px_rgba(251,191,36,0.6)] scale-105'
                      : 'bg-gradient-to-br from-[#1C2541] to-[#2A3B5C] text-[#EAEAEA] border-2 border-amber-400/30 hover:border-amber-400/60'
                  }`}
                >
                  <span className="block sm:inline">📋 {customMonth ? new Date(customMonth.year, customMonth.month - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Choisir Un Mois'}</span>
                  <div className="text-xs font-normal mt-1 opacity-80">
                    {customMonth ? 'Période sélectionnée' : 'Sélectionner...'}
                  </div>
                </button>
              </div>

              {/* Calendrier des Mois - CENTRÉ ET VISIBLE */}
              {showMonthDropdown && (
                <>
                  <div 
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[99998] animate-in fade-in duration-200 flex items-center justify-center p-4" 
                    onClick={() => setShowMonthDropdown(false)}
                  >
                    <div 
                      className="w-full max-w-[90vw] sm:max-w-[600px] md:max-w-[700px] max-h-[90vh] overflow-y-auto z-[99999] animate-in zoom-in-95 duration-300"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 rounded-3xl blur-2xl opacity-30 animate-pulse pointer-events-none"></div>
                      
                      <div className="relative bg-gradient-to-br from-[#1C2541] via-[#2A3B5C] to-[#1C2541] border-3 border-amber-400/60 rounded-3xl shadow-2xl overflow-hidden">
                        {/* Header Ultra Premium */}
                        <div className="px-4 sm:px-6 py-4 border-b-2 border-amber-400/40 bg-gradient-to-r from-amber-400/10 to-yellow-400/10 sticky top-0 z-20 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200">
                              📅 Choisir Un Mois
                            </h3>
                            <p className="text-amber-200/80 text-xs sm:text-sm mt-1">Sélectionnez le mois souhaité</p>
                          </div>
                          <button 
                            onClick={() => setShowMonthDropdown(false)}
                            className="text-[#EAEAEA] hover:text-amber-300 hover:bg-amber-300/10 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 text-3xl font-bold flex-shrink-0"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      
                      {/* Calendrier des Mois en Grille - Avec padding bottom */}
                      <div className="p-4 sm:p-6 pb-6 sm:pb-8">
                        {/* Année en Cours */}
                        <div className="mb-4 sm:mb-6">
                          <h4 className="text-amber-300 font-black text-base sm:text-lg mb-3 sm:mb-4 flex items-center gap-2">
                            <span className="text-xl sm:text-2xl">🗓️</span>
                            {new Date().getFullYear()}
                          </h4>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                            {[...Array(12)].map((_, i) => {
                              const monthIndex = i + 1;
                              const now = new Date();
                              const currentMonth = now.getMonth() + 1;
                              const currentYear = now.getFullYear();
                              
                              // Afficher UNIQUEMENT les mois déjà écoulés AVANT le mois précédent
                              // Exclure : mois en cours, mois précédent, et tous les mois futurs
                              const isExcluded = monthIndex >= (currentMonth - 1);
                              
                              if (isExcluded) return null;
                              
                              const monthName = new Date(currentYear, i, 1).toLocaleDateString('fr-FR', { month: 'long' });
                              const isSelected = customMonth?.year === currentYear && customMonth?.month === monthIndex;
                              
                              return (
                                <button
                                  key={monthIndex}
                                  onClick={() => {
                                    setCustomMonth({ year: currentYear, month: monthIndex });
                                    setSelectedMonth('custom');
                                    setShowMonthDropdown(false);
                                  }}
                                  className={`group relative px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105 ${
                                    isSelected
                                      ? 'bg-gradient-to-br from-amber-400 to-yellow-400 text-[#0B132B] shadow-lg shadow-amber-400/50'
                                      : 'bg-gradient-to-br from-[#2A3B5C]/50 to-[#1C2541]/50 text-[#EAEAEA] border-2 border-amber-400/30 hover:border-amber-400/60 hover:bg-gradient-to-br hover:from-amber-400/20 hover:to-yellow-400/20'
                                  }`}
                                >
                                  <div className="absolute inset-0 bg-gradient-to-r from-amber-400/0 via-amber-400/20 to-amber-400/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>
                                  <span className="relative capitalize">{monthName}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        
                        {/* Année Précédente (si applicable) */}
                        {new Date().getMonth() < 6 && (
                          <div className="pb-2">
                            <h4 className="text-amber-200 font-black text-base sm:text-lg mb-3 sm:mb-4 flex items-center gap-2">
                              <span className="text-xl sm:text-2xl">📆</span>
                              {new Date().getFullYear() - 1}
                            </h4>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                              {[...Array(12)].map((_, i) => {
                                const monthIndex = i + 1;
                                const lastYear = new Date().getFullYear() - 1;
                                
                                // Pour l'année précédente, afficher TOUS les 12 mois
                                // car ils sont tous déjà écoulés
                                
                                const monthName = new Date(lastYear, i, 1).toLocaleDateString('fr-FR', { month: 'long' });
                                const isSelected = customMonth?.year === lastYear && customMonth?.month === monthIndex;
                                
                                return (
                                  <button
                                    key={`${lastYear}-${monthIndex}`}
                                    onClick={() => {
                                      setCustomMonth({ year: lastYear, month: monthIndex });
                                      setSelectedMonth('custom');
                                      setShowMonthDropdown(false);
                                    }}
                                    className={`group relative px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105 ${
                                      isSelected
                                        ? 'bg-gradient-to-br from-amber-400 to-yellow-400 text-[#0B132B] shadow-lg shadow-amber-400/50'
                                        : 'bg-gradient-to-br from-[#2A3B5C]/50 to-[#1C2541]/50 text-[#EAEAEA] border-2 border-amber-400/30 hover:border-amber-400/60 hover:bg-gradient-to-br hover:from-amber-400/20 hover:to-yellow-400/20'
                                    }`}
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-amber-400/0 via-amber-400/20 to-amber-400/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>
                                    <span className="relative capitalize">{monthName}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </>
              )}

              {/* Mois Précédent - RESPONSIVE */}
              <button
                onClick={() => setSelectedMonth('previous')}
                className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-sm sm:text-base transition-all duration-300 transform hover:scale-105 ${
                  selectedMonth === 'previous'
                    ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 text-[#0B132B] shadow-[0_0_40px_rgba(251,191,36,0.6)] scale-105'
                    : 'bg-gradient-to-br from-[#1C2541] to-[#2A3B5C] text-[#EAEAEA] border-2 border-amber-400/30 hover:border-amber-400/60'
                }`}
              >
                <span className="block sm:inline">📆 Mois Précédent</span>
                <div className="text-xs font-normal mt-1 opacity-80">
                  {new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </div>
              </button>

              {/* Mois en Cours - RESPONSIVE */}
              <button
                onClick={() => setSelectedMonth('current')}
                className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-sm sm:text-base transition-all duration-300 transform hover:scale-105 ${
                  selectedMonth === 'current'
                    ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 text-[#0B132B] shadow-[0_0_40px_rgba(251,191,36,0.6)] scale-105'
                    : 'bg-gradient-to-br from-[#1C2541] to-[#2A3B5C] text-[#EAEAEA] border-2 border-amber-400/30 hover:border-amber-400/60'
                }`}
              >
                <span className="block sm:inline">📆 Mois en Cours</span>
                <div className="text-xs font-normal mt-1 opacity-80">
                  {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </div>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total Expenses Card - ULTRA PREMIUM - RESPONSIVE */}
      {!loading && (
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 rounded-2xl sm:rounded-3xl blur-xl group-hover:blur-2xl transition-all opacity-50 animate-pulse"></div>
          <Card className="relative backdrop-blur-2xl bg-gradient-to-br from-[#1C2541]/90 via-amber-900/10 to-[#1C2541]/90 border-2 sm:border-3 border-amber-400/60 shadow-2xl hover:shadow-amber-400/40 hover:scale-[1.01] sm:hover:scale-[1.02] transition-all duration-500">
            <CardContent className="p-4 sm:p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
                <div className="text-center md:text-left space-y-1 sm:space-y-2">
                  <div className="flex items-center justify-center md:justify-start gap-2 sm:gap-3">
                    <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400 animate-bounce" />
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200">
                      TOTAL DES DÉPENSES
                    </h2>
                  </div>
                  <p className="text-amber-200/70 text-base sm:text-lg font-semibold">
                    {expenses.length} dépense{expenses.length > 1 ? 's' : ''} enregistrée{expenses.length > 1 ? 's' : ''}
                  </p>
                </div>
                
                <div className="relative w-full md:w-auto">
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-xl sm:rounded-2xl blur-xl animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border-2 border-amber-300/50 shadow-2xl">
                    <div className="text-center">
                      <p className="text-amber-300 text-xs sm:text-sm font-bold uppercase tracking-widest mb-1 sm:mb-2">Montant Total</p>
                      <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 drop-shadow-[0_0_20px_rgba(252,211,77,0.8)] animate-pulse">
                        {totalExpenses.toLocaleString()}
                      </div>
                      <p className="text-xl sm:text-2xl md:text-3xl font-black text-amber-400 mt-1 sm:mt-2">FCFA</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="relative">
            <div className="w-24 h-24 border-8 border-amber-400/30 rounded-full"></div>
            <div className="w-24 h-24 border-8 border-amber-400 border-t-transparent rounded-full animate-spin absolute top-0"></div>
            <Sparkles className="w-8 h-8 text-amber-300 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
          </div>
        </div>
      ) : expenses.length === 0 ? (
        <Card className="backdrop-blur-xl bg-[#1C2541]/80 border-2 border-amber-400/30">
          <CardContent className="p-12 text-center">
            <Sparkles className="w-16 h-16 text-amber-400/50 mx-auto mb-4" />
            <p className="text-amber-200/70 text-xl font-semibold">Aucune dépense enregistrée</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {expenses.map((expense, index) => (
            <div key={expense.id} className="relative group" style={{ animationDelay: `${index * 50}ms` }}>
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-400/20 via-yellow-300/20 to-amber-400/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all opacity-0 group-hover:opacity-100"></div>
              
              <Card className="relative backdrop-blur-xl bg-gradient-to-br from-[#1C2541]/90 via-[#2A3B5C]/70 to-[#1C2541]/90 border-2 border-amber-400/30 hover:border-amber-400/60 shadow-xl hover:shadow-2xl hover:shadow-amber-400/20 hover:scale-[1.005] sm:hover:scale-[1.01] transition-all duration-300">
                <CardContent className="p-3 sm:p-4 md:p-6">
                  <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 sm:gap-4 lg:gap-6">
                    {/* Left side - Details - RESPONSIVE */}
                    <div className="flex-1 space-y-2 sm:space-y-3">
                      <div className="flex items-start justify-between gap-3 sm:gap-4">
                        <h3 className="text-lg sm:text-xl md:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200 flex items-center gap-2 sm:gap-3">
                          <span className="text-2xl sm:text-3xl">💰</span>
                          {expense.categorie}
                        </h3>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        <div className="flex items-center gap-2 sm:gap-3 bg-amber-900/20 backdrop-blur-sm p-2 sm:p-3 rounded-lg sm:rounded-xl border border-amber-400/20">
                          <span className="text-xl sm:text-2xl">📅</span>
                          <div>
                            <p className="text-amber-300/60 text-xs font-semibold uppercase">Date</p>
                            <p className="text-amber-100 text-xs sm:text-sm font-bold">{new Date(expense.date).toLocaleDateString('fr-FR', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 sm:gap-3 bg-amber-900/20 backdrop-blur-sm p-2 sm:p-3 rounded-lg sm:rounded-xl border border-amber-400/20">
                          <span className="text-xl sm:text-2xl">💳</span>
                          <div>
                            <p className="text-amber-300/60 text-xs font-semibold uppercase">Paiement</p>
                            <p className="text-amber-100 text-xs sm:text-sm font-bold">{expense.modePaiement}</p>
                          </div>
                        </div>
                      </div>
                      
                      {expense.note && (
                        <div className="bg-gradient-to-r from-amber-900/30 to-yellow-900/30 backdrop-blur-sm p-3 sm:p-4 rounded-lg sm:rounded-xl border border-amber-400/20">
                          <p className="text-amber-200/90 text-xs sm:text-sm leading-relaxed">
                            <span className="text-amber-400 font-bold">📝 Note:</span> {expense.note}
                          </p>
                        </div>
                      )}
                      
                      {expense.createdByName && (
                        <div className="flex items-center gap-2 text-amber-300 font-bold text-xs sm:text-sm">
                          <span className="text-lg sm:text-xl">👤</span>
                          <span>Dépensé par:</span>
                          <span className="text-amber-200">{expense.createdByName}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Right side - Amount & Actions - RESPONSIVE */}
                    <div className="flex flex-col items-stretch gap-3 sm:gap-4">
                      {/* Amount Display */}
                      <div className="relative group/amount">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-400/30 to-yellow-400/30 rounded-xl sm:rounded-2xl blur-lg group-hover/amount:blur-xl transition-all"></div>
                        <div className="relative bg-gradient-to-br from-amber-500/10 to-yellow-500/10 backdrop-blur-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-amber-400/40 shadow-xl text-center w-full sm:min-w-[180px] lg:min-w-[200px]">
                          <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-1 sm:mb-2">Montant</p>
                          <div className="text-2xl sm:text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 drop-shadow-[0_0_15px_rgba(252,211,77,0.6)]">
                            {expense.montant.toLocaleString()}
                          </div>
                          <p className="text-base sm:text-lg md:text-xl font-black text-amber-400 mt-1">FCFA</p>
                        </div>
                      </div>
                      
                      {/* Action Buttons - RESPONSIVE */}
                      {user?.role === 'admin' && (
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => handleEditExpense(expense)}
                            size="sm"
                            className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-[#0B132B] font-bold transition-all hover:scale-105 shadow-lg shadow-amber-400/30 border border-amber-300/50 text-xs sm:text-sm"
                          >
                            <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Modifier
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedExpense(expense);
                              setShowDeleteDialog(true);
                            }}
                            size="sm"
                            className="w-full bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-bold transition-all hover:scale-105 shadow-lg shadow-rose-500/30 text-xs sm:text-sm"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Supprimer
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="backdrop-blur-2xl bg-gradient-to-br from-[#1C2541]/95 to-[#2A3B5C]/95 border-2 border-amber-400/40 text-[#EAEAEA] shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200 flex items-center gap-2">
              <ReceiptIcon className="w-6 h-6 text-amber-400" />
              {isEditing ? 'Modifier la Dépense' : 'Nouvelle Dépense'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-amber-300 font-semibold">Catégorie</Label>
              <Select value={formData.categorie} onValueChange={(value) => setFormData({ ...formData, categorie: value })}>
                <SelectTrigger className="bg-[#0B132B]/80 border-amber-400/30 text-[#EAEAEA] hover:border-amber-400/60 transition-colors">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1C2541] border-amber-400/30 text-[#EAEAEA]">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.nom}>{cat.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-amber-300 font-semibold">Auteur de la dépense *</Label>
              <Select value={selectedAuthor} onValueChange={setSelectedAuthor}>
                <SelectTrigger className="bg-[#0B132B]/80 border-amber-400/30 text-[#EAEAEA] hover:border-amber-400/60 transition-colors">
                  <SelectValue placeholder="Sélectionner l'auteur..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1C2541] border-amber-400/30 text-[#EAEAEA]">
                  <SelectItem value="ceo">CEO (Admin)</SelectItem>
                  {commercials.map(commercial => (
                    <SelectItem key={commercial.id} value={commercial.id}>
                      {commercial.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-amber-300 font-semibold">Montant (FCFA) *</Label>
              <Input
                data-testid="montant-input"
                type="number"
                required
                value={formData.montant}
                onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                className="bg-[#0B132B]/80 border-amber-400/30 text-[#EAEAEA] focus:border-amber-400 transition-colors text-lg font-bold"
                placeholder="0"
              />
            </div>
            
            <div>
              <Label className="text-amber-300 font-semibold">Date *</Label>
              {isMobile ? (
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full bg-[#0B132B]/80 border-2 border-amber-400/30 text-[#EAEAEA] focus:border-amber-400 transition-colors text-base font-bold rounded-lg px-4 py-2.5 cursor-pointer"
                  data-testid="depenses-date-mobile"
                  style={{ colorScheme: 'dark' }}
                />
              ) : (
                <DatePicker
                  selected={new Date(formData.date)}
                  onChange={(date) => {
                    const formattedDate = date.toISOString().split('T')[0];
                    setFormData({ ...formData, date: formattedDate });
                  }}
                  dateFormat="dd/MM/yyyy"
                  locale="fr"
                  className="w-full bg-[#0B132B]/80 border-2 border-amber-400/30 text-[#EAEAEA] focus:border-amber-400 transition-colors text-base font-bold rounded-lg px-4 py-2 cursor-pointer"
                  calendarClassName="calendar-premium-popup"
                  showPopperArrow={false}
                  popperPlacement="bottom-start"
                  portalId="datepicker-portal-root"
                  popperProps={{ strategy: 'fixed' }}
                />
              )}
            </div>
            
            <div>
              <Label className="text-amber-300 font-semibold">Mode de paiement</Label>
              <Select value={formData.modePaiement} onValueChange={(value) => setFormData({ ...formData, modePaiement: value })}>
                <SelectTrigger className="bg-[#0B132B]/80 border-amber-400/30 text-[#EAEAEA] hover:border-amber-400/60 transition-colors">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1C2541] border-amber-400/30 text-[#EAEAEA]">
                  <SelectItem value="Especes">Espèces</SelectItem>
                  <SelectItem value="OrangeMoney">Orange Money</SelectItem>
                  <SelectItem value="MTNMoMo">MTN MoMo</SelectItem>
                  <SelectItem value="Wave">Wave</SelectItem>
                  <SelectItem value="Carte">Carte</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-amber-300 font-semibold">Note</Label>
              <Input
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                className="bg-[#0B132B]/80 border-amber-400/30 text-[#EAEAEA] focus:border-amber-400 transition-colors"
                placeholder="Description optionnelle"
              />
            </div>
            
            <Button
              data-testid="submit-expense-btn"
              type="submit"
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-[#0B132B] font-black text-lg py-6 shadow-xl shadow-amber-400/40 hover:scale-105 transition-all"
            >
              {isEditing ? 'Mettre à jour' : 'Enregistrer'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="backdrop-blur-2xl bg-gradient-to-br from-[#1C2541]/95 to-[#2A3B5C]/95 border-2 border-rose-500/40 text-[#EAEAEA] shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-rose-400 flex items-center gap-2">
              <Trash2 className="w-6 h-6" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA] text-lg">
              Êtes-vous sûr de vouloir supprimer cette dépense ?
            </p>
            {selectedExpense && (
              <div className="bg-[#0B132B]/80 p-4 rounded-xl border-2 border-amber-400/30">
                <p className="text-amber-300 font-black text-xl">{selectedExpense.categorie}</p>
                <p className="text-amber-100 text-2xl font-bold mt-2">{selectedExpense.montant.toLocaleString()} FCFA</p>
                <p className="text-amber-200/70 text-sm mt-1">{new Date(selectedExpense.date).toLocaleDateString('fr-FR')}</p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-amber-400/20 text-[#EAEAEA] font-bold border border-amber-400/30 hover:border-amber-400/60 transition-all"
              >
                Annuler
              </Button>
              <Button
                onClick={handleDelete}
                className="flex-1 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-bold shadow-lg shadow-rose-500/40 hover:scale-105 transition-all"
              >
                Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Depenses;
